import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Avatar, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, FormControlLabel, FormGroup, FormLabel, MenuItem, Radio, RadioGroup, TextField, Tooltip, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faPlug, faArrowRight, faUser, faTriangleExclamation, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { mcpScopeLabel, mcpScopeConstraints, mcpScopeNotices, sortMcpScopes } from '../utils/mcpScopes';
import { mcpApi, mcpErrorKey, pendingMcpRequest, clearMcpRequest, saveMcpRequest, validatedMcpCallback } from '../utils/mcpApi';
import { redirectToLogin } from '../utils/session';
import { clearRole, clearWorkspaceScopedSelections } from '../utils/permissions';
import { McpAuthFrame, McpBackLink, McpIntro, McpLoading } from '../components/McpUi';

const toggle = (values, value) => values.includes(value) ? values.filter(item => item !== value) : [...values, value];

// A permission's explanation sits next to it: ⚠ for cost, messages sent and deletion, ⓘ otherwise.
function ScopeNotice({ scope, t }) {
  const { keys, warn } = mcpScopeNotices(scope);
  if (!keys.length) return null;
  const text = keys.map(key => t(key)).join(' ');
  return <Tooltip title={text} placement="left" arrow enterTouchDelay={0} leaveTouchDelay={6000}>
    <span className={`mcp-scope-notice${warn ? ' is-warn' : ''}`} role="img" aria-label={text} tabIndex={0}>
      <FontAwesomeIcon icon={warn ? faTriangleExclamation : faCircleInfo} />
    </span>
  </Tooltip>;
}

export default function McpAuthorize() {
  const { t } = useTranslation();
  const [request] = useState(pendingMcpRequest);
  const [topLevel] = useState(() => window.self === window.top);
  const [consent, setConsent] = useState(null);
  const [workspace, setWorkspace] = useState('');
  const [scopes, setScopes] = useState([]);
  // Default (Robert, 19.10.2026): the first workspace with all its agents, including future ones.
  const [allAgents, setAllAgents] = useState(true);
  const [agents, setAgents] = useState([]);
  const [selected, setSelected] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentError, setAgentError] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [returning, setReturning] = useState(false);
  const [expired, setExpired] = useState(false);
  const [retry, setRetry] = useState(0);
  const [agentRetry, setAgentRetry] = useState(0);
  const [switchAccount, setSwitchAccount] = useState(false);
  const deciding = useRef(false);
  const agentRequest = useRef(null);
  const currentWorkspace = useRef(workspace);
  currentWorkspace.current = workspace;

  useEffect(() => {
    const controller = new AbortController();
    if (!topLevel) { setError('mcp_top_level'); return undefined; }
    if (!request) { setError('mcp_expired'); return undefined; }
    setError('');
    mcpApi('consent/inspect', { method: 'POST', body: JSON.stringify({ request }), signal: controller.signal })
      .then(data => {
        if (controller.signal.aborted) return;
        setConsent(data); setScopes(data.scopes);
        if (data.workspaces?.length) setWorkspace(String(data.workspaces[0].id));
      })
      .catch(err => { if (!controller.signal.aborted) setError(mcpErrorKey(err)); });
    return () => controller.abort();
  }, [request, topLevel, retry]);

  useEffect(() => {
    if (!consent?.expires_at) return undefined;
    const check = () => setExpired(Date.now() >= new Date(consent.expires_at).getTime());
    check();
    const timer = setInterval(check, 1000);
    return () => clearInterval(timer);
  }, [consent?.expires_at]);

  useEffect(() => {
    const controller = new AbortController();
    agentRequest.current = controller;
    setAgents([]); setSelected([]); setCursor(null); setAllAgents(true); setSearch(''); setAgentError(false);
    if (!workspace) { setAgentsLoading(false); return () => controller.abort(); }
    setAgentsLoading(true);
    mcpApi(`workspaces/${workspace}/agents`, { signal: controller.signal }).then(data => {
      if (!controller.signal.aborted) { setAgents(data?.agents || []); setCursor(data?.next_cursor || null); }
    }).catch(() => { if (!controller.signal.aborted) setAgentError(true); })
      .finally(() => { if (!controller.signal.aborted) setAgentsLoading(false); });
    return () => controller.abort();
  }, [workspace, agentRetry]);

  async function loadMore() {
    if (agentsLoading) return;
    const controller = agentRequest.current;
    const target = workspace;
    setAgentsLoading(true); setAgentError(false);
    try {
      const data = await mcpApi(`workspaces/${target}/agents?after=${encodeURIComponent(cursor)}`, { signal: controller.signal });
      if (!controller.signal.aborted && currentWorkspace.current === target) {
        setAgents(current => [...current, ...(data?.agents || [])]); setCursor(data?.next_cursor || null);
      }
    } catch { if (!controller.signal.aborted) setAgentError(true); }
    finally { if (!controller.signal.aborted) setAgentsLoading(false); }
  }

  const agentScope = scopes.includes('workspaces:delete') || scopes.some(scope => /^(agents|widget|knowledge|conversations|leads|reports|notifications|billing|integrations|channels|setup|products|cms|livechat):/.test(scope));
  const selectedWorkspace = consent?.workspaces.find(item => String(item.id) === workspace);
  const { missingRead, invalidRole } = mcpScopeConstraints(scopes, selectedWorkspace?.role, allAgents);
  const invalidMemberRole = workspace && scopes.includes('members:read') && !['owner', 'admin'].includes(selectedWorkspace?.role);
  const riskySelected = scopes.some(scope => mcpScopeNotices(scope).warn);
  const allScopes = sortMcpScopes(consent?.scopes);
  const terminal = expired || ['mcp_expired', 'mcp_top_level', 'mcp_restart_required'].includes(error);
  const canApprove = !!(consent && !terminal && !missingRead && !invalidRole && workspace && scopes.length && !agentsLoading && !agentError && (!agentScope || allAgents || selected.length));

  async function decide(approve) {
    if (deciding.current || terminal || (approve && !canApprove)) return;
    deciding.current = true; setBusy(true); setError('');
    let issued = false;
    try {
      const result = await mcpApi('consent/decide', { method: 'POST', body: JSON.stringify({
        request, approve, workspace_id: approve && workspace ? Number(workspace) : null,
        scopes: approve ? scopes : [], agent_ids: approve && allAgents ? null : approve ? selected : [],
      }) });
      issued = true;
      const callback = validatedMcpCallback(result.redirect_url, consent.redirect_uri);
      clearMcpRequest(); setReturning(true);
      window.location.assign(callback);
    } catch (err) {
      setError(issued ? 'mcp_restart_required' : mcpErrorKey(err)); setBusy(false); deciding.current = false;
    }
  }

  async function changeAccount() {
    if (deciding.current || terminal) return;
    deciding.current = true; setBusy(true); setError('');
    try {
      const result = await mcpApi('consent/restart', { method: 'POST', body: JSON.stringify({ request }) });
      saveMcpRequest(result.request);
      clearWorkspaceScopedSelections(); localStorage.removeItem('webflow_connected'); localStorage.removeItem('webflow_history');
      redirectToLogin({ clearRole });
    } catch (err) { setError(mcpErrorKey(err)); setBusy(false); deciding.current = false; setSwitchAccount(false); }
  }

  const showForm = consent && !terminal && !returning;

  return <McpAuthFrame cardClassName="mcp-consent">
    {/* fixed head: what is asking and who is asked */}
    <div className="mcp-consent-head">
      <div className="mcp-auth-heading"><span className="mcp-icon-tile"><FontAwesomeIcon icon={faPlug} /></span><McpIntro title={t('mcp_authorize_title')} subtitle={t('mcp_authorize_intro')} /></div>
      {showForm && <Box className="mcp-client-summary"><Typography component="h2" variant="h2">{consent.client_name}</Typography><Typography variant="body2" color="text.secondary">{t('mcp_unverified_client')}</Typography></Box>}
    </div>

    {/* scrolling middle */}
    <div className="mcp-consent-body">
      {returning ? <Box role="status" className="mcp-empty"><Typography variant="h2">{t('mcp_returning')}</Typography></Box> : <>
        {(error || expired) && <Alert severity={terminal ? 'warning' : 'error'} sx={{ mb: 2 }}>{t(expired ? 'mcp_expired' : error)}</Alert>}
        {!consent && !error && <McpLoading />}
        {!consent && error === 'mcp_error' && <Button onClick={() => setRetry(value => value + 1)}>{t('mcp_retry')}</Button>}
        {showForm && <>
          <div className="mcp-account-row"><Avatar sx={{ width: 32, height: 32, bgcolor: '#f3eaff', color: 'primary.main' }}><FontAwesomeIcon icon={faUser} style={{ fontSize: 13 }} /></Avatar><div><Typography variant="caption" color="text.secondary">{t('mcp_signed_in')}</Typography><Typography variant="body2">{consent.account?.email || consent.account?.name || localStorage.getItem('first_name')}</Typography></div><Button size="small" disabled={busy} onClick={() => setSwitchAccount(true)}>{t('mcp_switch_account')}</Button></div>

          <section className="mcp-form-section">
            <TextField select fullWidth size="small" id="mcp-workspace" label={t('mcp_workspace')} value={workspace} disabled={busy} onChange={e => { setWorkspace(e.target.value); setSelected([]); setAllAgents(true); setAgentsLoading(true); }} helperText={consent.workspaces.length ? undefined : t('mcp_no_workspaces')}>
              {consent.workspaces.map(item => <MenuItem key={item.id} value={String(item.id)}>{item.name}</MenuItem>)}
            </TextField>
          </section>

          <FormControl component="fieldset" disabled={busy} className="mcp-form-section mcp-scope-section" fullWidth>
            <FormLabel component="legend" className="mcp-scope-legend">
              <span>{t('mcp_permissions')}</span>
              <span className="mcp-scope-count">{t('mcp_scopes_selected', { selected: scopes.length, total: allScopes.length })}</span>
              <Button size="small" disabled={busy} onClick={() => setScopes(scopes.length === allScopes.length ? [] : allScopes)}>{t(scopes.length === allScopes.length ? 'mcp_select_none' : 'mcp_select_all')}</Button>
            </FormLabel>
            <FormGroup className="mcp-scope-list">{allScopes.map(scope => <div className="mcp-scope-row" key={scope}>
              <FormControlLabel label={mcpScopeLabel(scope, t)} control={<Checkbox size="small" checked={scopes.includes(scope)} onChange={() => setScopes(toggle(scopes, scope))} />} />
              <ScopeNotice scope={scope} t={t} />
            </div>)}</FormGroup>
            {riskySelected && <Typography className="mcp-help mcp-risk-line"><FontAwesomeIcon icon={faTriangleExclamation} /> {t('mcp_risky_summary')}</Typography>}
            {(missingRead || invalidRole || !scopes.length) && <Alert severity="info" sx={{ mt: 1 }}>{t(invalidMemberRole ? 'mcp_members_notice' : invalidRole ? 'mcp_role_notice' : missingRead ? 'mcp_read_required' : 'mcp_no_scopes')}</Alert>}
          </FormControl>

          {workspace && agentScope && <FormControl component="fieldset" disabled={busy} className="mcp-form-section" fullWidth>
            <FormLabel component="legend">{t('mcp_agent_access')}</FormLabel>
            <RadioGroup name="agent-access" value={allAgents ? 'all' : 'selected'} onChange={e => setAllAgents(e.target.value === 'all')}>
              <FormControlLabel value="all" control={<Radio size="small" />} label={t('mcp_all_agents')} />
              <FormControlLabel value="selected" control={<Radio size="small" />} label={t('mcp_selected_agents')} />
            </RadioGroup>
            {agentsLoading && <McpLoading />}
            {agentError && <Alert severity="error" action={<Button disabled={busy || agentsLoading} onClick={() => cursor ? loadMore() : setAgentRetry(value => value + 1)}>{t('mcp_retry')}</Button>}>{t('mcp_error')}</Alert>}
            {!allAgents && <Box className="mcp-agent-picker">
              {agents.length > 5 && <TextField fullWidth size="small" label={t('mcp_search_agents')} value={search} onChange={e => setSearch(e.target.value)} disabled={busy} />}
              <FormGroup className="mcp-agent-list">{agents.filter(agent => (agent.name || agent.id).toLocaleLowerCase().includes(search.toLocaleLowerCase())).map(agent => <FormControlLabel key={agent.id} label={agent.name || agent.id} control={<Checkbox size="small" disabled={busy || (selected.length >= 1000 && !selected.includes(agent.id))} checked={selected.includes(agent.id)} onChange={() => setSelected(toggle(selected, agent.id))} />} />)}</FormGroup>
              {!agents.length && !agentsLoading && !agentError && <Typography className="mcp-help">{t('mcp_no_agents')}</Typography>}
              {search && !agents.some(agent => (agent.name || agent.id).toLocaleLowerCase().includes(search.toLocaleLowerCase())) && <Typography className="mcp-help">{t('mcp_no_matches')}</Typography>}
              <div className="mcp-picker-footer"><Chip size="small" label={t('mcp_agents_count', { count: selected.length })} />{cursor && <Button disabled={busy || agentsLoading} onClick={loadMore}>{t('mcp_load_more')}</Button>}</div>
            </Box>}
          </FormControl>}

          <Accordion elevation={0} className="mcp-details" disableGutters><AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />}><Typography variant="body2">{t('mcp_details')}</Typography></AccordionSummary><AccordionDetails><Typography variant="caption" color="text.secondary">{t('mcp_callback')}</Typography><code>{consent.redirect_uri}</code></AccordionDetails></Accordion>
        </>}
        {terminal && <McpBackLink />}
      </>}
    </div>

    {/* fixed foot: always reachable */}
    {showForm && <div className="mcp-consent-foot">
      <Typography className="mcp-help">{t('mcp_consent_duration')}</Typography>
      <div className="mcp-actions"><Button variant="outlined" disabled={busy} onClick={() => decide(false)}>{t('mcp_deny')}</Button><Button variant="contained" disabled={busy || !canApprove} endIcon={!busy && <FontAwesomeIcon icon={faArrowRight} />} onClick={() => decide(true)}>{t(busy ? 'mcp_loading' : 'mcp_allow')}</Button></div>
    </div>}

    <Dialog open={switchAccount && !terminal} onClose={() => { if (!busy) setSwitchAccount(false); }} aria-labelledby="mcp-switch-title" fullWidth maxWidth="xs"><DialogTitle id="mcp-switch-title">{t('mcp_switch_account')}</DialogTitle><DialogContent><DialogContentText>{t('mcp_switch_intro')}</DialogContentText></DialogContent><DialogActions><Button disabled={busy} onClick={() => setSwitchAccount(false)}>{t('mcp_cancel')}</Button><Button variant="contained" disabled={busy} onClick={changeAccount}>{t('mcp_switch_account')}</Button></DialogActions></Dialog>
  </McpAuthFrame>;
}
